/**
 * Hide and Seek role rules editor: for hiders and for seekers, how often each
 * rule-governed ability (and dying) is allowed in the round — Off (None
 * allowed, for deaths), Unlimited, or Limited to a number of times.
 *
 * Used by the round setup form and the Round Events timeline (scheduled and
 * on-the-fly ruleset changes).
 */

import { memo, useId, useState } from 'react';
import {
  HNS_RULE_KEYS,
  HNS_RULE_LIMIT_MAX,
  HNS_RULE_LIMIT_MIN,
  formatHnsRuleAllowance,
  normalizeHnsRuleAllowance,
  type HnsRoleRules,
  type HnsRuleAllowance,
  type HnsRuleKey,
} from '@swtorbit/shared-socket-events';

type AllowanceMode = 'off' | 'unlimited' | 'limited';

const MODES: Array<{ mode: AllowanceMode; label: string }> = [
  { mode: 'off', label: 'Off' },
  { mode: 'unlimited', label: 'Unlimited' },
  { mode: 'limited', label: 'Limited' },
];

/** One rule for one role: Off / Unlimited / Limited, with the number of times when Limited. */
export const RuleAllowanceControl = memo(function RuleAllowanceControl({ name, rule, label, value, onChange }: {
  /** The radio group's name: unique on the page. */
  name: string;
  rule: HnsRuleKey;
  label: string;
  value: HnsRuleAllowance;
  onChange: (value: HnsRuleAllowance) => void;
}) {
  const mode: AllowanceMode = typeof value === 'number' ? 'limited' : value;
  // The number picking Limited again restores, after switching away from it.
  const [lastLimit, setLastLimit] = useState(typeof value === 'number' ? value : HNS_RULE_LIMIT_MIN);

  return (
    <div className="d-flex align-items-center justify-content-between gap-2xs flex-wrap">
      <span>{label}</span>
      <div className="d-flex align-items-center gap-2xs">
        <div className="segmented segmented--sm" role="radiogroup" aria-label={label}>
          {MODES.map(option => (
            <label key={option.mode} className="segmented__option">
              <input
                className="segmented__input"
                type="radio"
                name={name}
                checked={mode === option.mode}
                onChange={() => onChange(option.mode === 'limited' ? lastLimit : option.mode)}
              />
              {option.mode === 'off' ? formatHnsRuleAllowance('off', rule) : option.label}
            </label>
          ))}
        </div>
        {typeof value === 'number' && (
          <input
            className="input input--sm mw-4"
            type="number"
            min={HNS_RULE_LIMIT_MIN}
            max={HNS_RULE_LIMIT_MAX}
            step={1}
            value={value}
            aria-label={`${label}: times allowed`}
            onChange={(e) => {
              const limit = normalizeHnsRuleAllowance(Number(e.target.value));
              if (typeof limit !== 'number') return;
              setLastLimit(limit);
              onChange(limit);
            }}
          />
        )}
      </div>
    </div>
  );
});

/** One role's column of rules. */
const RoleRulesColumn = memo(function RoleRulesColumn({ title, idPrefix, rules, onChange }: {
  title: string;
  idPrefix: string;
  rules: HnsRoleRules;
  onChange: (rules: HnsRoleRules) => void;
}) {
  return (
    <div className="role-rules-col d-flex flex-column gap-2xs">
      <h4>{title}</h4>
      {HNS_RULE_KEYS.map(({ key, label }) => (
        <RuleAllowanceControl
          key={key}
          name={`${idPrefix}-${key}`}
          rule={key}
          label={label}
          value={rules[key]}
          onChange={(value) => onChange({ ...rules, [key]: value })}
        />
      ))}
    </div>
  );
});

/** Hider and seeker rules, side by side. */
export const RoleRulesEditor = memo(function RoleRulesEditor({ hiderRules, seekerRules, onHiderRulesChange, onSeekerRulesChange }: {
  hiderRules: HnsRoleRules;
  seekerRules: HnsRoleRules;
  onHiderRulesChange: (rules: HnsRoleRules) => void;
  onSeekerRulesChange: (rules: HnsRoleRules) => void;
}) {
  // Several editors can be on the page (setup form, each round's timeline).
  const id = useId();

  return (
    <div className="grid grid--1-2 gap-base">
      <RoleRulesColumn title="Hider Rules" idPrefix={`${id}-hider`} rules={hiderRules} onChange={onHiderRulesChange} />
      <RoleRulesColumn title="Seeker Rules" idPrefix={`${id}-seeker`} rules={seekerRules} onChange={onSeekerRulesChange} />
    </div>
  );
});
